// Shared non-component exports; keep component modules valid Fast Refresh boundaries.

import { type TimeRange } from "@/lib/api";
import { NZ_TIME_ZONE } from "@/lib/nz-time";

// Mirror of the backend's ``analytics._ALL_TIME_CHART_DAYS`` so the
// Assessments section uses the same fallback window the dashboard chart
// tiles already render for the default "All-time" range (Task #213).
// The backend treats All-time as ``[today - (N-1) days, today + 1 day)``
// in UTC; we resolve the same window on the client so the per-assessment
// queries — which are gated on a concrete from/to — actually fire.
export const ALL_TIME_CHART_DAYS = 90;

// T4 port (prototype Task #451) — ordered, non-custom time ranges used to
// warm the dashboard cache for the periods immediately adjacent to the
// active one. Switching Weekly ↔ Monthly (etc.) then resolves from cache
// instead of waiting on a fresh round-trip. ``custom`` has no defined
// neighbour so it's omitted.
export const ADJACENT_PREFETCH_RANGES: TimeRange[] = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "six_month",
  "all_time",
];

export function resolveAssessmentsRange(
  timeRange: TimeRange,
  period: { start: string | null; end: string | null } | null | undefined,
  now: Date = new Date(),
): { from: string | null; to: string | null } {
  if (timeRange !== "all_time") {
    return { from: period?.start ?? null, to: period?.end ?? null };
  }
  // Match ``_chart_window``: anchor on NZ "today" and return
  // ``[today - (N-1) days, today + 1 day)`` as YYYY-MM-DD strings,
  // the same shape ``period.start``/``period.end`` use for bounded
  // ranges (``date.isoformat()`` on the API side).
  const nzFmt = new Intl.DateTimeFormat("en-NZ", {
    timeZone: NZ_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = nzFmt.formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  const today = new Date(Date.UTC(get("year"), get("month") - 1, get("day")));
  const start = new Date(today);
  start.setUTCDate(start.getUTCDate() - (ALL_TIME_CHART_DAYS - 1));
  const end = new Date(today);
  end.setUTCDate(end.getUTCDate() + 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}
